import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/trpc/client";
import { AdFormData } from "../types";
import { track, LogEvents } from "@/analytics/track";

export const useCreateAd = (businessProfile: any) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createAd = async (formData: AdFormData) => {
    setIsProcessing(true);

    try {
      const numericSalary = formData.salary.replace(/\D/g, "");

      const newAdData: Record<string, unknown> = {
        businessUid: businessProfile?.uid,
        business: businessProfile?.business,
        adVersion: "v2",
        specialAd: null,
        orderNumber: 0,
        adActive: true,
        isDraft: false,
        adType: "automated",

        generalMenuData: {
          typeOfRoles: {
            Butikssäljare: true,
            Innesäljare: true,
            Utesäljare: true,
          },
          experience: formData.experience,
          formOfEmployment: formData.formOfEmployment,
          region: { remote: formData.isRemote, regionInner: formData.location },
          prefAges: {
            "18-25 år": true,
            "25-35 år": true,
            "35-45 år": true,
            "45-55 år": true,
            "55+ år": true,
          },
          driversLicense: formData.driversLicense,
        },

        tagsMenuData: {
          tags: formData.selectedTags,
          tagsMatchLimit: 0,
        },

        aboutTheAdData: {
          roleTitle: formData.roleTitle,
          descriptionOfRole: formData.jobDescription,
          descriptionOfRoleRaw: formData.descriptionOfRoleRaw,
          salaryExpectation: numericSalary || "30000",
          salaryMode: "showAll",
          requestedStartDate: "Omgående",
          payTransparency: formData.payTransparency,
        },

        benefitsMenuData: {
          Kollektivavtal: formData.selectedBenefits.includes("Kollektivavtal"),
          Utbildning: formData.selectedBenefits.includes("Utbildning"),
          Friskvårdsbidrag: formData.selectedBenefits.includes("Friskvårdsbidrag"),
          Workation: formData.selectedBenefits.includes("Workation"),
          Hunddagis: formData.selectedBenefits.includes("Hunddagis"),
          Bonus: formData.selectedBenefits.includes("Bonus"),
          "30 dagars semester": formData.selectedBenefits.includes("30 dagars semester"),
          Kompetensutveckling: formData.selectedBenefits.includes("Kompetensutveckling"),
          Företagsaktiviteter: formData.selectedBenefits.includes("Företagsaktiviteter"),
          Tjänstebil: formData.selectedBenefits.includes("Tjänstebil"),
          Lunchbidrag: formData.selectedBenefits.includes("Lunchbidrag"),
          Provision: formData.selectedBenefits.includes("Provision"),
          Sjukvårdsbidrag: formData.selectedBenefits.includes("Sjukvårdsbidrag"),
          Optionsprogram: formData.selectedBenefits.includes("Optionsprogram"),
        },

        languageSkillsMenuData: {
          swedish: formData.swedishLevel,
          english: formData.englishLevel,
        },

        workTasksMenuData: {
          "Plocka fram varor": true,
          "Kalla samtal": true,
          "Uppsökande försäljning": true,
          Exponering: true,
          "Ombesörja befintliga kunder": true,
          Prospektering: true,
          Administration: true,
          "Fysiska möten": true,
          Kundsupport: true,
          Personalansvar: true,
          Budgetansvar: true,
          Mötesbokning: true,
          Provision: true,
          Prismärkning: true,
          "Fylla på varor": true,
          Städning: true,
          Kassa: true,
        },

        chatMenuData: {
          isActivated: true,
          qualifyingQuestions: formData.qualifyingQuestions,
          firstMessage:
            "Tack för ditt intresse! Innan vi går vidare har vi några frågor för att säkerställa att rollen är en bra matchning.",
          firstResponsePositive: formData.qualifyingQuestions[0]?.positiveResponse || "Perfekt! Vi söker just denna kompetens.",
          firstResponseNegative: formData.qualifyingQuestions[0]?.negativeResponse || "Tyvärr är denna erfarenhet ett krav för tjänsten.",
          firstMessageOnNegative: formData.qualifyingQuestions[0]?.onNegativeResponse || "Tyvärr är denna erfarenhet ett krav för tjänsten.",
          firstMessageOnPositive: formData.qualifyingQuestions[0]?.onPositiveResponse || "Perfekt! Vi söker just denna kompetens.",
        },

        businessData: businessProfile,
      };

      const result = await trpcClient.jobAd.create.mutate({ data: newAdData });

      try {
        const notifyResult = await trpcClient.jobAd.sendNewAdNotifications.mutate({
          jobAdId: result.id,
        });
        if (notifyResult?.skipped && notifyResult.reason === "rate_limited") {
          console.info("Notifications rate-limited for new ad:", result.id);
        }
      } catch (error) {
        console.error("Error sending push notifications for new ad:", error);
      }

      track({
        event: LogEvents.JobAdCreated.name,
        channel: LogEvents.JobAdCreated.channel,
        ad_id: result.id,
      });

      await queryClient.invalidateQueries({ queryKey: [["jobAd", "list"]] });
      navigate(`/recruit/ads/${result.id}`);
      return result.id;
    } catch (error) {
      console.error("Error creating job ad:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    createAd,
    isProcessing,
  };
};

export default useCreateAd;
